/* eslint-disable max-lines-per-function */
import { captureMessage } from '@sentry/browser';
import features from 'Cloud/Application/Features';
import { confirmActionWithSuspiciousItems } from 'Cloud/Application/Suspicious';
import config from 'Cloud/config';
import { path } from 'ramda';
import { IS_MOBILE_BROWSER, IS_MY_TEAM, IS_PUBLIC_ALBUM, IS_REACT_PAGE } from 'reactApp/appHelpers/configHelpers';
import {EAttachTypes} from 'reactApp/modules/attaches/attaches.types';
import { getSelectedFaceId } from 'reactApp/modules/faces/faces.selectors';
import { fileController } from 'reactApp/modules/file/file.controller';
import multiDownload, { downloadByIframe, downloadByLink, isBlockedItem, isVirusItem } from 'reactApp/modules/file/utils';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { showVirusDlg } from 'reactApp/modules/popup/popup.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getPageSubtitle } from 'reactApp/modules/storage/storage.helpers';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { store as reduxStore } from 'reactApp/store';
import { ActionName } from 'reactApp/ui/VirusDialog/VirusDialog.types';
import { sendXray } from 'reactApp/utils/ga';
import { parseURL } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

const maxFilesCount = features.isFeature('multi-dwnld') && features.getFeatureParam('multi-dwnld', 'maxFilesCount', 10);

export default function Download(app, _) {
    const IS_MOBILE_CHROME = config.get('IS_MOBILE_CHROME');
    const MOBILE_BROWSER = config.get('MOBILE_BROWSER');
    const IS_IOS = config.get('IS_IOS');

    function download(url, downloadName, async) {
        // в chrome на ios не работает скачивание просматриваемых файлов через iframe, поэтому скачиваем через ссылку
        if ((!async && IS_MOBILE_CHROME && (MOBILE_BROWSER === 'IOS' || IS_IOS)) || IS_MY_TEAM) {
            if (!downloadName) {
                downloadName = parseURL(url).pathname.split('/').pop();
            }
            downloadByLink(url, downloadName);
        } else {
            downloadByIframe(url);
        }
    }

    const downloadItem = function (url, name, storage) {
        download(url, name, false);

        const state = reduxStore.getState();
        const isFaceFilterActive = Boolean(getSelectedFaceId(state));
        sendPaymentGa({
            eventCategory: ECategoryGa.download,
            action: 'download-files',
            type_downloads: 'one-file',
            source: IS_PUBLIC_ALBUM ? 'album' : storage,
            place: isFaceFilterActive ? 'face_list' : '',
        });
        sendXray(`app_download_${storage}`);
    };

    Object.assign(app, {
        download(idOrItem, forceDownload, useDownloadUrl, storage, fromViewer) {
            const id = typeof idOrItem === 'object' ? idOrItem.id : idOrItem;

            let item = typeof idOrItem === 'object' ? idOrItem : null;

            storage = storage || item?.storage;

            const storageForRadar = IS_REACT_PAGE ? storage.replace('/', '-') : _.sid().replace('/', '-');
            sendXray(`app_download-try_${storageForRadar}`);

            if (storage) {
                item = getStorageItemById(reduxStore.getState(), storage, id);
            } else if (!IS_REACT_PAGE) {
                item = this.get(idOrItem);
            } else if (!item) {
                item = getHomeItemById(reduxStore.getState(), id);
            }

            if (isBlockedItem(item)) {
                snackbarController.showSnackbar({
                    id: 'blocked',
                    text: 'Данный контент нарушает закон РФ и запрещён к распространению и использованию',
                    type: SnackbarTypes.failure,
                    closable: true,
                });
                return;
            }

            const isVirus = isVirusItem(item);

            if (isVirus && !forceDownload) {
                reduxStore.dispatch(showVirusDlg({ items: [item], doNotChangeRoute: fromViewer }));
                sendXray(`app_download_virus_${storageForRadar}`);
            } else {
                const { type, kind, url: itemUrl } = item;
                const download = itemUrl?.download;
                const get = itemUrl?.get;
                const isAttaches = storage === EStorageType.attaches || storage === EStorageType.viewerAttaches;

                let url = ((useDownloadUrl || isAttaches) && download) || get;

                const isCloudAttachType = type === EAttachTypes.cloud || type === EAttachTypes.cloudStock;

                if ((isCloudAttachType) && kind === 'image') {
                    url = (useDownloadUrl && download) || get;
                    sendXray(`download_attach_${type}_type_${useDownloadUrl ? 'jpeg' : 'original'}`);
                }

                if (isVirus) {
                    // пользователь подтвердил скачивание
                    // зараженного файла из приватного облака
                    downloadItem(url, item.name, storageForRadar);
                } else {
                    confirmActionWithSuspiciousItems(item, 'download')
                        .then(function () {
                            downloadItem(url, item.name, storageForRadar);
                        });
                }
            }
        },

        downloadItems(name, idsOrItems, forceDownload) {
            const state = reduxStore.getState();
            const storage = getCurrentStorage(state);
            const storageForRadar = storage.replace('/', '-');
            const isFaceFilterActive = Boolean(getSelectedFaceId(state));
            sendXray(`app_zip-try_${storageForRadar}`);

            if (!Array.isArray(idsOrItems)) {
                idsOrItems = [idsOrItems];
            }

            sendPaymentGa({
                eventCategory: ECategoryGa.download,
                action: 'download-files',
                type_downloads: idsOrItems.length === 1 ? 'one-folder' : 'files',
                source: IS_PUBLIC_ALBUM ? 'album' : storage,
                place: isFaceFilterActive ? 'face_list' : '',
            });

            const originalItems = idsOrItems;

            if (storage === EStorageType.stock) {
                name = getPageSubtitle();
            }

            const isVirus = IS_REACT_PAGE || app.isPublic() ? idsOrItems.some(isVirusItem) : app.isVirus(idsOrItems);

            function showVirusDialog() {
                reduxStore.dispatch(showVirusDlg({ items: originalItems, actionName: ActionName.zip, name }));
            }

            if (isVirus && !forceDownload) {
                showVirusDialog();
                sendXray(`app_zip_virus_${storageForRadar}`);
            } else {
                const makeZip = function (ids) {
                    fileController
                        .downloadFiles(ids, name, storage)
                        .then((url) => {
                            sendXray([`zip-download${storageForRadar}`, 'ok']);
                            download(url, `${name}.zip`, true);
                        })
                        .catch(() => {
                            sendXray([`zip-download${storageForRadar}`, 'error']);
                            snackbarController.showSnackbar({
                                id: 'apierror',
                                text: 'Ошибка сервера',
                                type: SnackbarTypes.failure,
                                closable: true,
                            });
                        });
                };

                const items =
                    IS_REACT_PAGE || app.isPublic() || isFaceFilterActive
                        ? originalItems
                        : idsOrItems.map(function (itemId) {
                              return app.get(itemId);
                          });

                const originalItemCount = items.length;

                confirmActionWithSuspiciousItems(items, 'download')
                    .then(function (items) {
                        if (IS_MOBILE_BROWSER && originalItemCount < maxFilesCount) {
                            const urlData = items
                                .map((item) => ({
                                    url: path(['url', 'get'], item),
                                    name: path(['name'], item),
                                }))
                                .filter((item) => !!item.url);

                            sendXray(['download', 'multi', 'start']);

                            multiDownload(urlData)
                                .then(() => {
                                    sendXray(['download', 'multi', 'ok']);
                                })
                                .catch((error) => {
                                    sendXray(['download', 'multi', 'error']);
                                    snackbarController.showSnackbar({
                                        id: 'cancel',
                                        text: 'Не удалось скачать файлы',
                                        type: SnackbarTypes.failure,
                                        closable: true,
                                    });
                                    captureMessage('download multi exception', { extra: error });
                                });
                        } else {
                            const ids = items.map((item) => item?.id || '').filter(Boolean);

                            makeZip(ids);
                        }
                    })
                    .catch(function () {
                        if (originalItemCount > 1) {
                            snackbarController.showSnackbar({
                                id: 'cancel',
                                text: 'Вы отменили скачивание всех файлов',
                                type: SnackbarTypes.failure,
                                closable: true,
                            });
                        }
                    });
            }
        },

        downloadItem,
    });
}
